import React from 'react'
import { useQuery } from '@apollo/client'
import { get, minBy } from 'lodash'
import Box from '@material-ui/core/Box'
import Chip from '@material-ui/core/Chip'
import Dialog from '@material-ui/core/Dialog'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useTheme } from '@material-ui/core/styles'
import PlaceIcon from 'components/material-symbol/icons/Place'
import ScheduleOutlinedIcon from 'components/material-symbol/icons/Schedule'
import CreditCardIcon from 'components/material-symbol/icons/CreditCard'
import LinkIcon from 'components/material-symbol/icons/Link'
import LockOutlinedIcon from 'components/material-symbol/icons/Lock'
import CopyTextButton from 'components/copy-text-button'
import EventDescription from 'pages/pga-coach/bookings/events/event-details-dialog/event-details-modal/event-description'
import useIconAccessor from 'components/lesson-type-category/icons'
import { centsToDollarsFormatted } from 'lib/utils/number'
import { MY_PGA_COM_HOST } from 'env'
import { durationText } from 'utils/durationUtils'
import { getPaymentAccountWarningChip } from 'pages/pga-coach/bookings/events/utils'
import { GET_LESSON_TYPE_DESCRIPTION } from '../queries'
import ActionButtons from './action-buttons'
import IconBadge from './icon-badge'
import { useStyles } from './styles'

export const formatBookingTypeUrl = lessonType => {
  if (!lessonType) {
    return null
  }

  const coachId = lessonType.coach.externalId
  const coachSlug = get(lessonType, 'coach.coachProfile.slug')
  const lessonTypeId = lessonType.id
  const locationId = get(lessonType, 'locations[0].id')

  return `${MY_PGA_COM_HOST}/coach/${coachSlug ||
    coachId}/schedule?lessonTypeId=${lessonTypeId}&locationId=${locationId}`
}

const getDisplayPrice = bookingType => {
  if (bookingType.lessonPacks?.length > 0) {
    const lowestPricePack = minBy(bookingType.lessonPacks, 'priceInCents')
    return centsToDollarsFormatted(lowestPricePack.priceInCents)
  }

  if (bookingType.priceInCents) {
    return centsToDollarsFormatted(bookingType.priceInCents)
  }

  return 'Free'
}

const getDurationLabel = bookingType => {
  const lessonPacksLength = bookingType.lessonPacks?.length ?? 0

  if (lessonPacksLength === 0) {
    return durationText(bookingType.durationInMinutes)
  }

  const packSummary = bookingType.lessonPacks
    .map(pack => `${pack.quantity}-pack`)
    .join(', ')

  return `${packSummary} · ${durationText(
    bookingType.durationInMinutes,
  )} / lesson`
}

const getStatusLabel = bookingType =>
  bookingType.status === 'INACTIVE' ? 'Private' : 'Active'

const getFeatureTags = bookingType => {
  const tags = []

  if (bookingType.accessCode) {
    tags.push('Access code required')
  }
  if (bookingType.giftingEnabled) {
    tags.push('Giftable')
  }
  if (bookingType.lessonPacks?.length > 0) {
    tags.push('Lesson packs available')
  }

  return tags
}

const BookingTypeDetailsContent = ({
  bookingType,
  setIsOpen,
  isLessonPack = false,
  readOnly = false,
}) => {
  const classes = useStyles()
  const accessor = useIconAccessor()
  const category = accessor[bookingType.category] || accessor.OTHER
  const bookingTypeUrl = formatBookingTypeUrl(bookingType)
  const isExpired =
    bookingType.expiresAt && Date.parse(bookingType.expiresAt) < Date.now()
  const canEdit = !readOnly && !isLessonPack && !isExpired
  const paymentAccountWarningChip = readOnly
    ? null
    : getPaymentAccountWarningChip({
      event: bookingType,
      classes,
      isMobile: true,
    })
  const featureTags = readOnly ? [] : getFeatureTags(bookingType)
  const locationNames = bookingType.locations?.map(location => location.name)
  const description = bookingType.description?.trim()

  return (
    <>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Box className={classes.hero}>
          <ActionButtons
            setIsOpen={setIsOpen}
            bookingType={bookingType}
            bookingTypeUrl={bookingTypeUrl}
            canEdit={canEdit}
            readOnly={readOnly}
          />
          <Box className={classes.heroContent}>
            {!readOnly ? (
              <Chip
                label={getStatusLabel(bookingType)}
                size="small"
                className={classes.statusChip}
              />
            ) : null}
            <Typography className={classes.heroTitle}>
              {bookingType.title}
            </Typography>
            <Typography className={classes.heroSubtitle}>
              {category.text}
            </Typography>
          </Box>
          <Box className={classes.heroPrice}>
            <Typography className={classes.heroPriceLabel}>PRICE</Typography>
            <Typography className={classes.heroPriceValue}>
              {getDisplayPrice(bookingType)}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent className={classes.contentRoot}>
        <Grid container spacing={0} className={classes.contentGrid}>
          <Grid item xs={12} md={5} className={classes.aboutColumn}>
            <Box className={classes.mainColumn}>
              <Box>
                <Typography className={classes.sectionLabel}>
                  About this offering
                </Typography>
                {description ? (
                  <EventDescription description={description} />
                ) : (
                  <Typography className={classes.detailSecondary}>
                    {readOnly
                      ? 'Purchase lesson packs or book sessions with your coach through PGA Coach.'
                      : bookingType.lessonPacks?.length > 0
                        ? 'Students can purchase lesson packs or book individual sessions through your PGA Coach schedule.'
                        : 'Students can book this offering directly through your PGA Coach schedule.'}
                  </Typography>
                )}
              </Box>
              {featureTags.length > 0 ? (
                <Box className={classes.tagRow}>
                  {featureTags.map(tag => (
                    <Chip
                      key={tag}
                      label={tag}
                      size="small"
                      className={classes.tagChip}
                    />
                  ))}
                </Box>
              ) : null}
            </Box>
          </Grid>
          <Grid item xs={12} md={7} className={classes.detailsColumn}>
            <Box className={classes.sidebar}>
              <Box className={classes.detailsCard}>
                <Typography className={classes.sectionLabel}>
                  Booking type details
                </Typography>
                <Box className={classes.detailRow}>
                  <IconBadge icon={ScheduleOutlinedIcon} />
                  <Box>
                    <Typography className={classes.detailPrimary}>
                      Session length
                    </Typography>
                    <Typography className={classes.detailSecondary}>
                      {getDurationLabel(bookingType)}
                    </Typography>
                  </Box>
                </Box>
                <Box className={classes.detailRow}>
                  <IconBadge icon={PlaceIcon} />
                  <Box minWidth={0}>
                    <Typography className={classes.detailPrimary}>
                      Location
                    </Typography>
                    <Typography className={classes.detailSecondary}>
                      {locationNames?.join(', ')}
                    </Typography>
                  </Box>
                </Box>
                <Box className={classes.detailRow}>
                  <IconBadge icon={CreditCardIcon} />
                  <Box>
                    <Typography className={classes.detailPrimary}>
                      Price
                    </Typography>
                    <Typography className={classes.detailSecondary}>
                      {getDisplayPrice(bookingType)}
                    </Typography>
                    {bookingType.lessonPacks?.length > 0 ? (
                      <Typography className={classes.detailSecondary}>
                        {bookingType.lessonPacks
                          .map(
                            pack =>
                              `${
                                pack.quantity
                              } lessons · ${centsToDollarsFormatted(
                                pack.priceInCents,
                              )}`,
                          )
                          .join(' · ')}
                      </Typography>
                    ) : (
                      <Typography className={classes.detailSecondary}>
                        per session
                      </Typography>
                    )}
                    {paymentAccountWarningChip ? (
                      <Box className={classes.paymentWarningRow}>
                        {paymentAccountWarningChip}
                      </Box>
                    ) : null}
                  </Box>
                </Box>
                {bookingType.accessCode ? (
                  <Box className={classes.detailRow}>
                    <IconBadge icon={LockOutlinedIcon} />
                    <Box>
                      <Typography className={classes.detailPrimary}>
                        Access code required
                      </Typography>
                      <Typography className={classes.detailSecondary}>
                        {readOnly
                          ? 'You will need an access code from your coach to book.'
                          : 'Students need a code from you to book.'}
                      </Typography>
                    </Box>
                  </Box>
                ) : null}
                {bookingTypeUrl && !readOnly ? (
                  <CopyTextButton
                    value={bookingTypeUrl}
                    className={classes.copyTextButtonStyled}
                  >
                    <Box className={classes.detailRow}>
                      <IconBadge icon={LinkIcon} />
                      <Typography className={classes.linkText}>
                        {bookingTypeUrl}
                      </Typography>
                    </Box>
                  </CopyTextButton>
                ) : null}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
    </>
  )
}

const BookingTypeDetailsDialog = ({
  isOpen,
  setIsOpen,
  bookingType,
  isLessonPack = false,
  readOnly = false,
}) => {
  const classes = useStyles()
  const isMobile = useMediaQuery(useTheme().breakpoints.down('sm'))
  const { data: descriptionData } = useQuery(GET_LESSON_TYPE_DESCRIPTION, {
    variables: { id: bookingType?.id },
    skip: !isOpen || !bookingType?.id,
    fetchPolicy: 'network-only',
  })

  if (!bookingType) {
    return null
  }

  const resolvedBookingType = {
    ...bookingType,
    description:
      descriptionData?.lessonTypes?.[0]?.description ?? bookingType.description,
  }

  return (
    <Dialog
      open={!!isOpen}
      onClose={() => setIsOpen(false)}
      fullScreen={isMobile}
      maxWidth="md"
      fullWidth
      classes={isMobile ? undefined : { paper: classes.dialogPaper }}
    >
      {isOpen ? (
        <BookingTypeDetailsContent
          bookingType={resolvedBookingType}
          setIsOpen={setIsOpen}
          isLessonPack={isLessonPack}
          readOnly={readOnly}
        />
      ) : null}
    </Dialog>
  )
}

export default BookingTypeDetailsDialog
